export type TopologyPresentationRow = { key: string; label: string; value: string };
export type TopologyPropertyRow = {
  key: string;
  label: string;
  value: string;
  depth: number;
  group: boolean;
};

export type TopologyPropertyRowsOptions = {
  excludeKeys?: readonly string[];
};

// These limits apply only to browser rendering. The full graph payload remains
// available from the backend; this prevents one unusually large property from
// turning a selection or interface panel into thousands of DOM rows.
export const TOPOLOGY_PROPERTY_MAX_ROWS = 160;
export const TOPOLOGY_PROPERTY_MAX_DEPTH = 4;
export const TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS = 24;
export const TOPOLOGY_PROPERTY_MAX_VALUE_LENGTH = 512;

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

export const topologyPresentationProps = (
  properties?: Record<string, unknown>
): Record<string, unknown> | undefined => {
  return asRecord(properties?.presentation);
};

export const formatTopologyPresentationValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatTopologyPresentationValue).filter((item) => item !== '—').join(', ');
  }
  return '';
};

export const topologyPresentationRows = (
  properties?: Record<string, unknown>
): TopologyPresentationRow[] | null => {
  const presentation = topologyPresentationProps(properties);
  if (!presentation) {
    return null;
  }

  const rows = presentation.rows;
  if (Array.isArray(rows)) {
    return rows
      .map((row, index) => {
        const rec = asRecord(row);
        if (!rec) {
          return null;
        }
        const label = getString(rec.label) ?? getString(rec.key) ?? '';
        const value = formatTopologyPresentationValue(rec.value);
        if (!label.trim() || !value.trim()) {
          return null;
        }
        return { key: `${getString(rec.key) ?? label}:${index}`, label, value };
      })
      .filter((row): row is TopologyPresentationRow => Boolean(row));
  }

  const summary = asRecord(presentation.summary);
  if (summary) {
    return Object.entries(summary)
      .map(([key, value], index) => ({ key: `${key}:${index}`, label: key, value: formatTopologyPresentationValue(value) }))
      .filter((row) => row.value.trim().length > 0);
  }

  return null;
};

const sortedPropertyEntries = (properties: Record<string, unknown>): Array<[string, unknown]> =>
  Object.entries(properties).sort(([left], [right]) => left.localeCompare(right));

type propertyRowState = {
  rows: TopologyPropertyRow[];
  rowLimitReached: boolean;
};

const appendPropertySummary = (state: propertyRowState, key: string, depth: number, value: string): void => {
  if (state.rows.length >= TOPOLOGY_PROPERTY_MAX_ROWS) {
    // Reserve the final visible row for an honest truncation message instead
    // of silently dropping everything after the limit.
    state.rows[TOPOLOGY_PROPERTY_MAX_ROWS - 1] = { key, label: '…', value, depth, group: false };
    return;
  }
  state.rows.push({ key, label: '…', value, depth, group: false });
};

const formatBoundedPropertyValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    const items = value.slice(0, TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS).map((item) => {
      if (asRecord(item) || Array.isArray(item)) {
        return '[complex value]';
      }
      return formatTopologyPresentationValue(item);
    });
    if (value.length > TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS) {
      items.push(`… ${value.length - TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS} more`);
    }
    return items.filter(Boolean).join(', ') || '[]';
  }
  const formatted = formatTopologyPropertyValue(value);
  if (formatted.length <= TOPOLOGY_PROPERTY_MAX_VALUE_LENGTH) {
    return formatted;
  }
  return `${formatted.slice(0, TOPOLOGY_PROPERTY_MAX_VALUE_LENGTH)}… (${formatted.length - TOPOLOGY_PROPERTY_MAX_VALUE_LENGTH} more characters)`;
};

const appendTopologyPropertyRows = (
  state: propertyRowState,
  key: string,
  label: string,
  value: unknown,
  depth: number
): void => {
  if (state.rows.length >= TOPOLOGY_PROPERTY_MAX_ROWS) {
    state.rowLimitReached = true;
    return;
  }
  const record = asRecord(value);
  if (record) {
    state.rows.push({ key, label, value: '', depth, group: true });
    const entries = sortedPropertyEntries(record);
    if (depth >= TOPOLOGY_PROPERTY_MAX_DEPTH) {
      appendPropertySummary(state, `${key}.__depth_limit`, depth + 1, `${entries.length} nested properties not expanded`);
      return;
    }
    for (const [childKey, childValue] of entries) {
      appendTopologyPropertyRows(state, `${key}.${childKey}`, childKey, childValue, depth + 1);
      if (state.rowLimitReached) {
        return;
      }
    }
    return;
  }

  const objectArray = Array.isArray(value) && value.length > 0 && value.slice(0, TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS).every((item) => asRecord(item));
  if (objectArray) {
    state.rows.push({ key, label, value: '', depth, group: true });
    const visibleItems = value.slice(0, TOPOLOGY_PROPERTY_MAX_ARRAY_ITEMS);
    if (depth >= TOPOLOGY_PROPERTY_MAX_DEPTH) {
      appendPropertySummary(state, `${key}.__depth_limit`, depth + 1, `${value.length} nested items not expanded`);
      return;
    }
    visibleItems.forEach((item, index) => {
      const itemRecord = asRecord(item);
      if (!itemRecord) {
        return;
      }
      const itemDepth = depth + 1;
      if (value.length > 1) {
        if (state.rows.length >= TOPOLOGY_PROPERTY_MAX_ROWS) {
          state.rowLimitReached = true;
          return;
        }
        state.rows.push({ key: `${key}.${index + 1}`, label: `${label} ${index + 1}`, value: '', depth: itemDepth, group: true });
      }
      sortedPropertyEntries(itemRecord).forEach(([childKey, childValue]) => {
        appendTopologyPropertyRows(state, `${key}.${index + 1}.${childKey}`, childKey, childValue, itemDepth + (value.length > 1 ? 1 : 0));
      });
    });
    if (value.length > visibleItems.length && !state.rowLimitReached) {
      appendPropertySummary(state, `${key}.__array_limit`, depth + 1, `${value.length - visibleItems.length} additional items not expanded`);
    }
    return;
  }

  state.rows.push({ key, label, value: formatBoundedPropertyValue(value), depth, group: false });
};

// Properties are the neutral-model details contract. Presentation and ui are
// rendering metadata, not domain facts, so a generic details view excludes
// them while preserving every resolved graph property. Nested objects are
// expanded generically so no graph family needs a custom UI formatter.
export const topologyPropertyRows = (properties?: Record<string, unknown>, options: TopologyPropertyRowsOptions = {}): TopologyPropertyRow[] => {
  const state: propertyRowState = { rows: [], rowLimitReached: false };
  const excluded = new Set(['presentation', 'ui', ...(options.excludeKeys ?? [])]);
  sortedPropertyEntries(properties ?? {})
    .filter(([key]) => !excluded.has(key))
    .forEach(([key, value]) => appendTopologyPropertyRows(state, key, key, value, 0));
  if (state.rowLimitReached) {
    appendPropertySummary(state, '__row_limit', 0, 'Additional properties not expanded');
  }
  return state.rows;
};

// A backend may intentionally send only the curated presentation rows and no
// raw domain properties. The generic selection panel can render those rows as
// its detail fallback without needing to know the graph family or provider.
export const topologySelectionPropertyRows = (
  properties?: Record<string, unknown>,
  options: TopologyPropertyRowsOptions = {}
): TopologyPropertyRow[] => {
  const domainRows = topologyPropertyRows(properties, options);
  if (domainRows.length > 0) {
    return domainRows;
  }
  return (topologyPresentationRows(properties) ?? []).map((row) => ({
    ...row,
    depth: 0,
    group: false
  }));
};

export const formatTopologyPropertyValue = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return '—';
  }
  const scalar = formatTopologyPresentationValue(value);
  if (scalar) {
    return scalar;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const topologyPresentationString = (
  properties: Record<string, unknown> | undefined,
  key: 'title' | 'hoverTitle' | 'subtitle' | 'selectionTitle' | 'selectionSubtitle',
  fallback: string
): string => {
  const presentation = topologyPresentationProps(properties);
  const value = presentation?.[key];
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (key === 'title' || key === 'hoverTitle' || key === 'selectionTitle') {
    return getString(presentation?.title) ?? fallback;
  }
  return fallback;
};
